import useNumberFormatter from "@/hooks/useNumberFormatter";
import {
  Badge,
  Box,
  HStack,
  SegmentGroup,
  Stack,
  Text,
} from "@chakra-ui/react";
import { Check } from "lucide-react";

// Fianza integral options, in COP (0 = not added). Must match the backend's
// INTEGRAL_AMOUNTS (protect/data/contracts.ts).
export const INTEGRAL_OPTIONS = [0, 1_000_000, 2_000_000] as const;
export type IntegralOption = (typeof INTEGRAL_OPTIONS)[number];

// Cláusula penal in months of rent (0 = not added), up to the backend's
// MAX_PENALTY_MONTHS.
export const PENALTY_MONTH_OPTIONS = [0, 1, 2, 3] as const;
export type PenaltyMonthsOption = (typeof PENALTY_MONTH_OPTIONS)[number];

type Props = {
  monthlyRent: number;
  rentCoverageMonths?: number;
  integralAmount: IntegralOption;
  onIntegralAmountChange: (value: IntegralOption) => void;
  penaltyMonths: PenaltyMonthsOption;
  onPenaltyMonthsChange: (value: PenaltyMonthsOption) => void;
};

type RowProps = {
  title: string;
  description: React.ReactNode;
  control: React.ReactNode;
};

// One coverage line: what it covers on the left, the choice on the right.
const CoverageRow = ({ title, description, control }: RowProps) => (
  <Stack
    direction={{ base: "column", sm: "row" }}
    justify="space-between"
    align={{ base: "stretch", sm: "center" }}
    gap={2}
    px={3}
    py={3}
  >
    <Box minW={0}>
      <Text fontWeight={600} fontSize="sm">
        {title}
      </Text>
      <Text fontSize="xs" color="gray.500">
        {description}
      </Text>
    </Box>
    <Box flexShrink={0}>{control}</Box>
  </Stack>
);

// A segmented choice over numeric options (SegmentGroup works with strings).
const NumberSegments = <T extends number>({
  label,
  options,
  value,
  onChange,
  format,
}: {
  label: string;
  options: readonly T[];
  value: T;
  onChange: (value: T) => void;
  format: (option: T) => string;
}) => (
  <SegmentGroup.Root
    size="sm"
    aria-label={label}
    value={String(value)}
    onValueChange={(e) => {
      const next = options.find((option) => String(option) === e.value);
      if (next !== undefined) onChange(next);
    }}
  >
    <SegmentGroup.Indicator />
    <SegmentGroup.Items
      items={options.map((option) => ({
        value: String(option),
        label: format(option),
      }))}
    />
  </SegmentGroup.Root>
);

const CoverageLines = ({
  monthlyRent,
  rentCoverageMonths,
  integralAmount,
  onIntegralAmountChange,
  penaltyMonths,
  onPenaltyMonthsChange,
}: Props): JSX.Element => {
  const formatCurrency = useNumberFormatter("es-CO", "COP", "currency");

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      css={{ "& > *:not(:last-child)": { borderBottom: "1px solid #EDF2F7" } }}
    >
      <CoverageRow
        title="Canon de arrendamiento"
        description={
          rentCoverageMonths
            ? `Hasta ${rentCoverageMonths} meses`
            : "Cobertura base de la fianza"
        }
        control={
          <Badge bg="#E3FFE4" color="#1D7A5C" size="md" gap={1}>
            <Check size="0.8rem" />
            Incluida
          </Badge>
        }
      />
      <CoverageRow
        title="Fianza integral"
        description="Daños, inventario y servicios"
        control={
          <NumberSegments
            label="Fianza integral"
            options={INTEGRAL_OPTIONS}
            value={integralAmount}
            onChange={onIntegralAmountChange}
            format={(amount) => (amount === 0 ? "No" : formatCurrency(amount))}
          />
        }
      />
      <CoverageRow
        title="Cláusula penal"
        description={
          <>
            Abandono en mora
            {penaltyMonths > 0 && (
              <Text as="span" display="block" color="gray.700">
                Valor afianzado:{" "}
                <Text as="span" fontWeight={600}>
                  {formatCurrency(monthlyRent * penaltyMonths)}
                </Text>
              </Text>
            )}
          </>
        }
        control={
          <HStack gap={2}>
            <NumberSegments
              label="Cláusula penal en cánones"
              options={PENALTY_MONTH_OPTIONS}
              value={penaltyMonths}
              onChange={onPenaltyMonthsChange}
              format={(months) => (months === 0 ? "No" : String(months))}
            />
            <Text fontSize="xs" color="gray.500">
              cánones
            </Text>
          </HStack>
        }
      />
    </Box>
  );
};

export default CoverageLines;
